import type { CollaboratorContractType, FunctionalRoleCode } from './project';

/** 组织项目页预算树分组：团队自有项目 / 承制项目 */
export type OrgProjectBudgetScope = 'team' | 'contract';

/**
 * 组织（多主体）相关类型定义
 */

// ── 枚举 ────────────────────────────────────────────────────────

/** 组织类型：1=企业, 2=独立团队 */
export type OrgType = 1 | 2;

/** 组织状态：0=待激活, 1=正常, 2=已暂停, 3=已解散 */
export type OrgStatus = 0 | 1 | 2 | 3;

/**
 * 成员角色：1=OWNER, 2=ADMIN, 3=MEMBER
 *
 * 命名约定：代码层沿用业界通用术语 OWNER/ADMIN/MEMBER（与后端
 * `OrgMemberRole` 枚举、API 契约 `ownerUserId`/`isOwner` 一致）；
 * 用户可见的中文文案见下方 `ORG_MEMBER_ROLE_LABELS`，对应：
 *   1 (OWNER)  → "超级管理员"
 *   2 (ADMIN)  → "管理员"
 *   3 (MEMBER) → "普通成员"
 */
export type OrgMemberRole = 1 | 2 | 3;

/** 成员状态：0=非活跃, 1=活跃, 2=已暂停 */
export type OrgMemberStatus = 0 | 1 | 2;

export type RoleTemplateCode = 'producer' | 'executive_producer' | 'finance';

export type OrgReportAccessScope = 0 | 1 | 2;

export const ORG_REPORT_ACCESS_SCOPE_LABELS: Record<OrgReportAccessScope, string> = {
  0: '不允许查看',
  1: '仅查看参与的项目统计数据',
  2: '能查看团队全量统计数据',
};

/** 邀请类型：1=链接邀请, 2=手机号邀请, 3=项目外部成员, 4=邮箱邀请 */
export type InvitationType = 1 | 2 | 3 | 4;

/** 邀请状态：0=待确认, 1=已接受, 2=已拒绝, 3=已过期, 4=已撤销, 5=待团队审核, 6=团队拒绝, 7=待项目审核, 8=项目拒绝 */
export type InvitationStatus = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** 认证状态：0=草稿, 1=待审核, 2=审核中, 3=已通过, 4=已拒绝, 5=待补材料 */
export type CertStatus = 0 | 1 | 2 | 3 | 4 | 5;

/** 企业解散申请状态：1=待审核, 2=审核中, 3=已通过, 4=已拒绝, 5=已取消, 6=已执行 */
export type OrgDissolutionRequestStatus = 1 | 2 | 3 | 4 | 5 | 6;

/** 分拨状态：0=活跃, 1=已撤回, 2=已耗尽 */
export type AllocationStatus = 0 | 1 | 2;

// ── 组织 ────────────────────────────────────────────────────────

export interface Org {
  orgId: string;
  orgType: OrgType;
  orgCode: string;
  name: string;
  avatarUrl?: string | null;
  description?: string | null;
  ownerUserId: string;
  status: OrgStatus;
  aiCreditsBalance: number | null;
  totalRechargedAmount: string | null;
  totalCreditsEarned: number | null;
  totalCreditsConsumed: number | null;
  memberCount: number;
  maxMemberCount: number;
  maxProjectCount?: number | null;
  projectLimit?: number;
  activeRootProjectCount?: number;
  creditsAlertThreshold: number | null;
  canViewCreditBalance?: boolean;
}

export interface OrgPublicProfile {
  orgId: string;
  orgCode: string;
  orgType: OrgType;
  name: string;
  avatarUrl?: string | null;
  /** 公开展示名，不等同于组织内真实姓名 */
  ownerDisplayName?: string | null;
  externalCollaborationCoordinatorLabel: string;
}

export interface OrgCodeLookupState {
  input: string;
  normalizedCode: string | null;
  profile: OrgPublicProfile | null;
  error: string | null;
}

export interface CreateTeamDto {
  name: string;
  avatarUrl?: string;
  description?: string;
}

export interface UpdateOrgDto {
  name?: string;
  avatarUrl?: string;
  description?: string;
}

export interface TransferOwnerDto {
  newOwnerUserId: string;
}

export interface LeaveOrgResult {
  status: 'left';
}

export interface OrgDissolutionRequest {
  requestId: string;
  orgId: string;
  applicantUserId: string;
  status: OrgDissolutionRequestStatus;
  reason?: string | null;
  orgNameSnapshot: string;
  orgCodeSnapshot: string;
  ownerUserIdSnapshot: string;
  reviewerAdminId?: string | null;
  reviewNote?: string | null;
  rejectionReason?: string | null;
  reviewedAt?: string | null;
  projectAction?: 'migrate' | 'purge' | null;
  affectedProjects: number;
  executedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrgDissolutionExecuteResult {
  requestId: string;
  orgId: string;
  affectedProjects: number;
}

// ── 成员 ────────────────────────────────────────────────────────

export interface OrgMember {
  id: string;
  orgId: string;
  userId: string;
  role: OrgMemberRole;
  status: OrgMemberStatus;
  /** 当前团队/企业内维护的真实姓名，不是用户全局资料 */
  realName?: string | null;
  invitedBy?: string | null;
  joinedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** 参与项目数（从列表接口附带） */
  projectCount?: number | null;
  reportAccessScope?: OrgReportAccessScope;
  roleTemplateCode?: RoleTemplateCode | null;
  customGroupIds?: string[];
  positionIds?: string[];
  /** 制片人预算额度（credits 单位）；null 表示当前用户无权查看 */
  producerCreditLimit?: number | null;
  /** 制片人已分拨预算（credits 单位）；null 表示当前用户无权查看 */
  producerCreditAllocated?: number | null;
  /** 制片人可用预算（credits 单位）；null 表示当前用户无权查看 */
  producerCreditAvailable?: number | null;
  /** 用户信息（从列表接口附带） */
  user?: {
    userId: string;
    username: string;
    nickname?: string | null;
    avatarUrl?: string | null;
    phone?: string | null;
    email?: string | null;
  };
}

export interface PendingOrgInvitation {
  invitationId: string;
  orgId: string;
  inviterUserId: string;
  invitationType: InvitationType;
  inviteePhone?: string | null;
  inviteeEmail?: string | null;
  inviteeUserId?: string | null;
  targetGroupId?: string | null;
  reportAccessScope?: OrgReportAccessScope;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  inviteeUser?: {
    userId: string;
    username: string;
    nickname?: string | null;
    avatarUrl?: string | null;
    phone?: string | null;
    email?: string | null;
  };
}

export interface MemberListStats {
  activeMemberCount: number;
  projectParticipatingMemberCount: number;
  pendingInvitationCount: number;
}

export interface MemberListResponse {
  items: OrgMember[];
  pendingInvitations: PendingOrgInvitation[];
  total: number;
  page?: number;
  pageSize?: number;
  stats?: MemberListStats;
}

export interface UpdateMemberRoleDto {
  role: OrgMemberRole;
}

export interface UpdateMemberIdentityDto {
  orgRole?: OrgMemberRole;
  roleTemplateCode?: RoleTemplateCode | null;
  customGroupIds?: string[];
}

export interface UpdateMemberRealNameDto {
  realName?: string | null;
}

export interface UpdateReportAccessScopeDto {
  reportAccessScope: OrgReportAccessScope;
}

export interface MemberRealName {
  userId: string;
  realName?: string | null;
}

export interface MemberIdentity {
  userId: string;
  orgRole: OrgMemberRole;
  roleTemplateCode?: RoleTemplateCode | null;
  customGroupIds: string[];
  warning?: string | null;
}

export interface MemberProjectResidue {
  projectMemberCount: number;
  functionalRoleCount: number;
  leadOfProjectIds: string[];
}

export interface OrgMemberProjectFunctionalRole {
  code: FunctionalRoleCode;
  label: string;
  canManageMembers: boolean;
  canCreateScreenplays?: boolean;
}

export interface OrgMemberProjectTreeNode {
  projectId: string;
  projectName: string;
  parentProjectId?: string | null;
  hasSubprojects: boolean;
  isEpn: boolean;
  status?: 'active' | 'paused' | 'archived' | 'trashed' | 'soft_deleted' | string;
  createdAt: string;
  projectRole?: 'owner' | 'editor' | 'viewer' | string | null;
  isDirectMember: boolean;
  functionalRoles: OrgMemberProjectFunctionalRole[];
  subprojects: OrgMemberProjectTreeNode[];
}

export interface OrgMemberProjectTreeResponse {
  items: OrgMemberProjectTreeNode[];
  totalDirectProjectCount: number;
  totalVisibleNodeCount: number;
}

export interface EnterprisePosition {
  positionId: string;
  orgId: string;
  code: string;
  name: string;
  description?: string | null;
  memberCount: number;
}

export interface CreateEnterprisePositionDto {
  name: string;
  description?: string | null;
}

export interface UpdateEnterprisePositionDto {
  name: string;
  description?: string | null;
}

// ── 邀请 ────────────────────────────────────────────────────────

export interface OrgInvitation {
  invitationId: string;
  orgId: string;
  inviterUserId: string;
  invitationType: InvitationType;
  token?: string | null;
  inviteePhone?: string | null;
  inviteeEmail?: string | null;
  inviteeUserId?: string | null;
  targetGroupId?: string | null;
  reportAccessScope?: OrgReportAccessScope;
  targetProjectId?: string | null;
  projectRole?: number | null;
  status: InvitationStatus;
  acceptedAt?: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvitationPreview {
  orgId: string;
  orgName: string;
  orgType: OrgType;
  orgAvatarUrl?: string | null;
  inviterName: string;
  invitationType: InvitationType;
  targetProjectId?: string | null;
  projectName?: string | null;
  projectRole?: number | null;
  expiresAt: string;
  memberCount: number;
  maxMemberCount: number;
}

export interface CreateLinkInvitationDto {
  targetGroupId?: string;
  reportAccessScope?: OrgReportAccessScope;
  expiresInMinutes?: number;
}

export interface CreatePhoneInvitationDto {
  inviteePhone: string;
  targetGroupId?: string;
  reportAccessScope?: OrgReportAccessScope;
}

export interface CreateEmailInvitationDto {
  inviteeEmail: string;
  targetGroupId?: string;
  reportAccessScope?: OrgReportAccessScope;
}

export interface ExternalProjectPhoneInvitationDto {
  inviteePhone: string;
  expiresInHours?: number;
}

export interface ExternalProjectEmailInvitationDto {
  inviteeEmail: string;
  expiresInHours?: number;
}

export interface AcceptInvitationDto {
  token?: string;
  invitationId?: string;
}

export interface ExternalProjectInvitation {
  invitationId: string;
  orgId: string;
  orgName?: string | null;
  projectId: string;
  projectName?: string | null;
  inviterUserId: string;
  inviteeUserId?: string | null;
  inviteePhone?: string | null;
  inviteeEmail?: string | null;
  inviteeName?: string | null;
  token?: string | null;
  status: InvitationStatus;
  projectRole?: number | null;
  permissionGroupIds: string[];
  functionalRoleCodes: string[];
  canManageMembers: boolean;
  acceptedAt?: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExternalProjectInvitationListResponse {
  items: ExternalProjectInvitation[];
  total: number;
}

export interface ExternalProjectInvitationReviewDto {
  approveOrg: boolean;
  approveProject?: boolean | null;
  projectRole?: 'editor' | 'viewer';
  permissionGroupIds?: string[];
  groupIds?: string[];
  functionalRoles?: string[];
  canManageMembers?: boolean;
  note?: string | null;
}

// ── 权限组 ──────────────────────────────────────────────────────

export interface PermGroup {
  groupId: string;
  orgId: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  isRoleTemplate?: boolean;
  templateCode?: RoleTemplateCode | null;
  permissions: string[];
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePermGroupDto {
  name: string;
  description?: string;
  permissions?: string[];
}

export interface UpdatePermGroupDto {
  description?: string;
  permissions?: string[];
}

export interface PermissionItem {
  code: string;
  label: string;
  description: string;
  /** 仅超级管理员（OWNER）可用，不支持通过权限组分配 */
  ownerOnly?: boolean;
  /** 仅系统权限角色模板可携带，不支持通过自定义权限组分配 */
  roleTemplateOnly?: boolean;
  /** 是否允许企业自定义权限组选择 */
  customAssignable?: boolean;
}

export interface PermissionDomain {
  domain: string;
  label: string;
  permissions: PermissionItem[];
}

// ── 词元 ────────────────────────────────────────────────────────

export interface OrgCreditBalance {
  orgId: string;
  aiCreditsBalance: number;
  totalRechargedAmount: number;
  totalCreditsEarned: number;
  totalCreditsConsumed: number;
  creditsAlertThreshold: number;
}

export interface BudgetOverview {
  projectId: string;
  projectName: string;
  /** 预算算力 */
  aiCreditsBudget: number;
  /** 已耗算力示值（源自 token 累计） */
  budgetConsumed: number;
  /** 剩余预算算力 */
  budgetRemaining: number;
  memberCount: number;
  hasSubprojects?: boolean;
}

/**
 * RFC-003 / 父持总池（RFC-006 §6.5）：项目预算树节点。
 * - 子 EPN：`isEpn=true`，`aiCreditsBudget` 为本集实际预算（算力），`budgetConsumed` 为已耗算力示值。
 * - 聚合父行：`hasSubprojects=true`、`isEpn=false`；`aiCreditsBudget` 为单集模板，`totalCreditsBudget` 为父项目总授信。
 * - 无子根行：`subtotal*` 同自身行数据。
 */
export interface BudgetTreeProgress {
  total: number;
  completed: number;
  percent: number;
}

export interface BudgetTreeNode {
  projectId: string;
  projectName: string;
  status?: 'active' | 'paused' | 'archived' | 'trashed' | 'soft_deleted';
  /** 项目创建时间（ISO），用于列表排序 */
  createdAt?: string;
  parentProjectId: string | null;
  hasSubprojects: boolean;
  isEpn: boolean;
  aiCreditsBudget: number;
  totalCreditsBudget: number;
  createdSubprojectBudgetTotal: number;
  createdSubprojectCount: number;
  budgetConsumed: number;
  budgetRemaining: number;
  creditAllocationPending?: boolean;
  /** 当前承制方组织在该节点范围内是否已有成员职能配置 */
  hasConfiguredMembers?: boolean;
  memberCount: number;
  /** 聚合父行下所有子项目成员去重数；子 EPN 等于自身成员数 */
  uniqueMemberCount?: number;
  subtotalBudget: number;
  subtotalConsumed: number;
  subtotalRemaining: number;
  subtotalMemberCount: number;
  /** 聚合父行下所有子项目成员去重数；用于父级成员数主展示 */
  subtotalUniqueMemberCount?: number;
  subprojects: BudgetTreeNode[];
  /** 单集所在项目内文件夹。仅 includeFolders 请求返回。 */
  folderId?: string | null;
  /** 父项目下的项目内文件夹。仅 includeFolders 请求返回。 */
  folders?: Array<{
    id: string;
    name: string;
    parentFolderId?: string | null;
    color?: string | null;
    description?: string | null;
    sortOrder?: number | null;
    createdAt?: string | null;
  }>;
  /** 子树内是否存在未撤销的跨组织协作（PCO） */
  hasOutgoingOrgCollab?: boolean;
  /** 子树内 PCO 条数（服务端已按父子汇总） */
  outgoingPcoCount?: number;
  /** 当前节点或子树内是否存在已生成的承制内容清单报告 */
  hasContractorSettlementReports?: boolean;
  /** 资产库完成进度；report-only 或无内容权限时为空 */
  assetProgress?: BudgetTreeProgress | null;
  /** 视频完成进度；report-only 或无内容权限时为空 */
  videoProgress?: BudgetTreeProgress | null;
  /** 已解除的承制项目仅保留报告入口，不允许进入项目或展示预算/成员数据 */
  contractorReportOnly?: boolean;
  /** 组织项目页数据源：team=团队项目，contract=承制项目（active incoming PCO） */
  projectScope?: OrgProjectBudgetScope;
  /** 承制项目绑定的 PCO ID（全包承制预算实例键） */
  pcoId?: string | null;
  /** 承制类型：1=制作承制，2=全包承制 */
  contractType?: CollaboratorContractType | null;
  /** 主办方组织名称（承制项目列表展示） */
  hostOrgName?: string | null;
  /** 主办方组织 ID（承制父项目成员管理等前端语义判断） */
  hostOrgId?: string | null;
  /** 全包承制：本承制方预算是否已配置（父项目预算 > 0） */
  contractBudgetConfigured?: boolean;
  /** 全包承制：当前用户是否可配置承制预算（credits.allocate） */
  canManageContractBudget?: boolean;
  /** 承制方超级管理员是否可管理承制成员 */
  canManageContractMembers?: boolean;
}

/** 全包承制：为 PCO 项目分拨/调整父项目总预算 */
export interface ContractProjectBudgetAllocateDto {
  pcoId: string;
  projectId: string;
  amount: number;
  note?: string;
}

/** 全包承制：按项目撤回剩余承制预算 */
export interface ContractProjectBudgetRecallDto {
  pcoId: string;
  projectId: string;
  recallAmount?: number;
}

/** 全包承制：更新单集实际预算或父项目单集默认预算 */
export interface ContractProjectBudgetPatchDto {
  pcoId: string;
  projectId: string;
  aiCreditsBudget: number;
}

/** 全包承制：同父项目下两单集间调拨承制预算 */
export interface ContractEpisodeCreditTransferDto {
  pcoId: string;
  fromProjectId: string;
  toProjectId: string;
  amount: number;
}

export interface CreditAllocation {
  allocationId: string;
  orgId: string;
  projectId: string;
  producerUserId?: string | null;
  operatorUserId: string;
  amount: number;
  note?: string | null;
  status: AllocationStatus;
  recalledAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BudgetAllocateDto {
  projectId: string;
  amount: number;
  note?: string;
  producerUserId?: string;
}

/** 父级下多子 EPN 批量授予项目预算 */
export interface BudgetAllocateBatchDto {
  parentProjectId: string;
  items: { projectId: string; amount: number }[];
  producerUserId?: string;
  note?: string;
}

/** 同父下两子 EPN 间移动项目预算 */
export interface EpisodeCreditTransferDto {
  fromProjectId: string;
  toProjectId: string;
  amount: number;
}

export interface BudgetRecallDto {
  allocationId: string;
  recallAmount?: number;
}

export interface AlertThresholdDto {
  threshold: number;
}

export interface RecallByProjectDto {
  projectId: string;
  recallAmount?: number;
}

// ── 算力明细 ──────────────────────────────────────────────────────

export interface OrgCreditLedgerItem {
  eventId: string;
  /** recharge | gift | expire | admin_adjust */
  eventType: string;
  amount: number;
  balanceAfter?: number | null;
  projectId?: string | null;
  projectName?: string | null;
  operatorUserId?: string | null;
  operatorNickname?: string | null;
  note?: string | null;
  createdAt: string;
}

export interface OrgCreditLedgerList {
  items: OrgCreditLedgerItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalRecharge: number;
  totalGift: number;
  totalExpired: number;
  totalAdminAdjust: number;
}

// ── Token 消耗 ────────────────────────────────────────────────────

export interface OrgTokenConsumption {
  transactionId: string;
  /** consume | refund */
  eventType: string;
  /** transaction | private_migration_summary */
  source?: 'transaction' | 'private_migration_summary' | string;
  /** false 表示仅用于展示，不参与组织余额/报表记账 */
  isAccounting?: boolean;
  featureType?: number | null;
  balanceBefore?: number | null;
  balanceAfter?: number | null;
  trackType?: number | null;
  trackTypeName?: string | null;
  amount: number;
  projectId?: string | null;
  projectName?: string | null;
  operatorUserId: string;
  operatorNickname?: string | null;
  operatorDisplayName?: string | null;
  description?: string | null;
  createdAt: string;
  billingState?: 'in_progress' | 'settling' | 'settled' | null;
  heldCredits?: number;
  actualUsage?: number | null;
  callCount?: number | null;
  runId?: string | null;
}

export interface OrgTokenConsumptionList {
  items: OrgTokenConsumption[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalConsumed: number;
  totalRefunded: number;
  heldCredits?: number;
  privateMigrationConsumed?: number | null;
  privateMigrationVisible: boolean;
  privateMigrationVisibilityReason?: 'role' | 'private_owner' | 'forbidden' | string | null;
  scope: 'organization' | 'self' | 'producer_projects';
  balanceVisible: boolean;
}

export interface OrgCreditLedgerParams {
  page?: number;
  pageSize?: number;
  eventType?: string;
}

// ── 制片人预算明细 ───────────────────────────────────────────────

export type ProducerCreditLedgerEventType =
  | 'producer_limit_set'
  | 'project_allocate'
  | 'project_recall'
  | 'project_consume'
  | 'project_refund'
  | 'project_correction';

export interface BudgetConsumptionMetadata {
  source?: 'transaction' | 'ai_operation' | 'ai_usage_correction';
  billingState?: 'in_progress' | 'settling' | 'settled' | null;
  heldCredits?: number;
  runId?: string | null;
}

export interface ProducerCreditLedgerItem extends BudgetConsumptionMetadata {
  eventId: string;
  eventType: ProducerCreditLedgerEventType;
  budgetScope?: 'team' | 'contract' | null;
  amount: number;
  producerAvailableAfter?: number | null;
  projectRemainingAfter?: number | null;
  producerUserId?: string | null;
  producerNickname?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  operatorUserId?: string | null;
  operatorNickname?: string | null;
  note?: string | null;
  createdAt: string;
}

export interface ProducerCreditLedgerProducerRow {
  eventId: string;
  eventType: ProducerCreditLedgerEventType;
  createdAt: string;
  producerUserId?: string | null;
  producerNickname?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  operatorUserId?: string | null;
  operatorNickname?: string | null;
  note?: string | null;
  creditIn: number;
  allocatedOut: number;
  returnedIn: number;
  available?: number | null;
}

export interface ProducerCreditLedgerProjectRow extends BudgetConsumptionMetadata {
  eventId: string;
  eventType: ProjectBudgetLedgerEventType;
  budgetScope?: 'team' | 'contract' | null;
  createdAt: string;
  projectId?: string | null;
  projectName?: string | null;
  producerUserId?: string | null;
  producerNickname?: string | null;
  operatorUserId?: string | null;
  operatorNickname?: string | null;
  note?: string | null;
  allocatedIn: number;
  consumedOut: number;
  recalledOut: number;
  refundedIn: number;
  remaining?: number | null;
  remainingSource?: 'project_pool' | 'parent_pool' | 'subproject_cap' | null;
  parentRemaining?: number | null;
  capLimit?: number | null;
  capRemaining?: number | null;
  /** 已人工核实的历史单集上限缺口。 */
  capHistoryMissing?: boolean;
}

export interface ProducerCreditLedgerList {
  items: ProducerCreditLedgerItem[];
  producerRows: ProducerCreditLedgerProducerRow[];
  projectRows: ProducerCreditLedgerProjectRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalLimit: number;
  totalAllocated: number;
  totalAvailable: number;
  totalProjectConsumed: number;
}

export interface ProducerCreditLedgerParams {
  view?: 'all' | 'producer' | 'project';
  page?: number;
  pageSize?: number;
  eventType?: ProducerCreditLedgerEventType;
  producerUserId?: string;
  projectId?: string;
  producerKeyword?: string;
  projectKeyword?: string;
}

// ── 项目预算明细 ───────────────────────────────────────────────

export type ProjectBudgetLedgerEventType =
  | 'project_allocate'
  | 'project_recall'
  | 'project_consume'
  | 'project_refund'
  | 'project_correction';

export interface ProjectBudgetLedgerRow extends BudgetConsumptionMetadata {
  eventId: string;
  eventType: ProjectBudgetLedgerEventType;
  budgetScope?: 'team' | 'contract' | null;
  createdAt: string;
  projectId?: string | null;
  projectName?: string | null;
  producerUserId?: string | null;
  producerNickname?: string | null;
  operatorUserId?: string | null;
  operatorNickname?: string | null;
  note?: string | null;
  allocatedIn: number;
  consumedOut: number;
  recalledOut: number;
  refundedIn: number;
  remaining?: number | null;
  remainingSource?: 'project_pool' | 'parent_pool' | 'subproject_cap' | null;
  parentRemaining?: number | null;
  capLimit?: number | null;
  capRemaining?: number | null;
  /** 已人工核实的历史单集上限缺口。 */
  capHistoryMissing?: boolean;
}

export interface ProjectBudgetLedgerList {
  items: ProjectBudgetLedgerRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalAllocated: number;
  totalRecalled: number;
  totalConsumed: number;
  totalRefunded: number;
}

export interface ProjectBudgetLedgerParams {
  page?: number;
  pageSize?: number;
  eventType?: ProjectBudgetLedgerEventType;
  producerUserId?: string;
  projectId?: string;
  projectKeyword?: string;
}

export interface OrgTokenConsumptionParams {
  page?: number;
  pageSize?: number;
  projectId?: string;
  operatorUserId?: string;
  trackType?: number;
  featureType?: number;
  /** 近 7/30/90/180 天；缺省由后端返回全量（admin 兼容） */
  range?: OrgReportsRange;
  /** 自定义开始日期 YYYY-MM-DD（本地自然日，日界由 tzOffset 决定）；与 range 互斥 */
  startDate?: string;
  /** 自定义结束日期 YYYY-MM-DD（本地自然日，日界由 tzOffset 决定）；与 range 互斥 */
  endDate?: string;
  /** 本地时区相对 UTC 的分钟偏移（东八区=480），随自定义日期一起传给后端 */
  tzOffset?: number;
}

// ── 操作记录 ──────────────────────────────────────────────────────

export interface OrgOperationLogItem {
  logId: string;
  orgId: string;
  actorUserId: string;
  actorDisplayName?: string | null;
  action: string;
  category: OrgOperationLogCategory;
  actionLabel: string;
  summary: string;
  detailItems: OrgOperationLogDetailItem[];
  severity: OrgOperationLogSeverity;
  resourceType?: string | null;
  resourceId?: string | null;
  targetUserId?: string | null;
  targetDisplayName?: string | null;
  /** 列表「目标」列可读文案（含被邀手机号、权限组名等），与 targetDisplayName 互补 */
  targetCaption?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export type OrgOperationLogCategory =
  | 'organization'
  | 'member'
  | 'invitation'
  | 'position'
  | 'permission'
  | 'temp_member'
  | 'project'
  | 'download';

export type OrgOperationLogSeverity = 'info' | 'warning' | 'danger';

export interface OrgOperationLogDetailItem {
  field: string;
  label: string;
  before?: string | null;
  after?: string | null;
  value?: string | null;
}

export interface OrgOperationLogList {
  items: OrgOperationLogItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface OrgOperationLogParams {
  page?: number;
  pageSize?: number;
  action?: string;
  category?: OrgOperationLogCategory;
  actorUserId?: string;
  targetUserId?: string;
  startTime?: string;
  endTime?: string;
}

// ── 统计报表（组织管理概览） ────────────────────────────────────────

/** 统计报表时间范围：7/30/90/180 天（滚动窗口，不含今日早于区间的部分） */
export type OrgReportsRange = '7d' | '30d' | '90d' | '180d';
export type OrgReportsScope = 'organization' | 'member_projects' | 'producer_projects';

export interface OrgReportsBalance {
  total: number;
  locked: number;
  available: number;
  alertThreshold: number;
}

export interface OrgReportsFeatureAmount {
  featureType: number;
  amount: number;
}

export interface OrgReportsMonthMTD {
  /** 自然月累计（本月 1 日 00:00 至当前） */
  creditConsumed: number;
  tokenConsumed: number;
  /** 环比 = 上一个完整自然月的同字段对比；>0 上升，<0 下降 */
  creditConsumedMoMVsLastMonthFull: number;
  featureConsumptionByType: OrgReportsFeatureAmount[];
  tokenByFeature: OrgReportsFeatureAmount[];
}

export interface OrgReportsRolling30d {
  /** 滚动 30 天累计（无论当前处在月哪一天，都往前推 30 天） */
  creditConsumed: number;
  tokenConsumed: number;
  /** 环比 = 再往前推 30 天 */
  creditConsumedMoMVsPrev30d: number;
}

export interface OrgReportsMembers {
  total: number;
  owner: number;
  admin: number;
  member: number;
  pendingInvites: number;
}

export interface OrgReportsProjects {
  total: number;
  active: number;
  archived: number;
  /** 已消耗 > 已分拨预算的项目数 */
  overBudget: number;
  /** 利用率 ≥ 85% 且未 overBudget 的项目数（即将超支） */
  nearLimit: number;
}

export interface OrgReportsCreditDaily {
  date: string;
  consumed: number;
  recharged: number;
}

export interface OrgReportsTokenDaily {
  date: string;
  /** key = feature_type，value = 该日该功能消耗量 */
  byFeature: Record<string, number>;
}

export interface OrgReportsTrend {
  creditDaily: OrgReportsCreditDaily[];
  featureConsumptionDaily: OrgReportsTokenDaily[];
  tokenDaily: OrgReportsTokenDaily[];
}

export interface OrgReportsTopProject {
  projectId: string;
  name: string;
  consumed: number;
  /** 利用率 = consumed / allocatedBudget（当预算为 0 时返回 null） */
  utilization: number | null;
}

export interface OrgReportsTopMember {
  userId: string;
  name: string;
  consumed: number;
}

export interface OrgReportsProducerCredit {
  total: number;
  producerCount: number;
}

export interface OrgReportsSummary {
  range: OrgReportsRange;
  scope: OrgReportsScope;
  canViewOrgBalance: boolean;
  balance: OrgReportsBalance | null;
  currentMonthMTD: OrgReportsMonthMTD;
  rolling30d: OrgReportsRolling30d;
  members: OrgReportsMembers;
  projects: OrgReportsProjects;
  trend: OrgReportsTrend;
  topProjects: OrgReportsTopProject[];
  topMembers: OrgReportsTopMember[];
  recentLedger: OrgCreditLedgerItem[];
  producerCredit: OrgReportsProducerCredit | null;
}

export interface OrgReportsSummaryParams {
  range?: OrgReportsRange;
}

// ── 企业认证 ─────────────────────────────────────────────────────

export interface Certification {
  certId: string;
  applicantUserId: string;
  submissionCount: number;
  orgId?: string | null;
  /** 审核通过后组织的企业码，与 organizations.org_code 一致 */
  orgCode?: string | null;
  businessName?: string | null;
  orgCategory?: number | null;
  businessLicenseNo?: string | null;
  licenseImageUrl?: string | null;
  licenseExpiresAt?: string | null;
  licenseIsLongTerm?: boolean | null;
  registeredAddress?: string | null;
  businessScopeSummary?: string | null;
  legalPersonName?: string | null;
  legalPersonIdNo?: string | null;
  legalPersonIdFrontUrl?: string | null;
  legalPersonIdBackUrl?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  companySize?: number | null;
  websiteUrl?: string | null;
  extraCertUrls?: string[] | null;
  usageDescription?: string | null;
  certStatus: CertStatus;
  reviewerAdminId?: string | null;
  reviewerDisplayName?: string | null;
  reviewedAt?: string | null;
  submittedAt?: string | null;
  rejectionReason?: string | null;
  rejectionCode?: number | null;
  supplementRequest?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CertDraftDto {
  businessName?: string;
  orgCategory?: number;
  businessLicenseNo?: string;
  licenseImageUrl?: string;
  licenseExpiresAt?: string | null;
  licenseIsLongTerm?: boolean;
  registeredAddress?: string;
  businessScopeSummary?: string;
  legalPersonName?: string;
  legalPersonIdNo?: string;
  legalPersonIdFrontUrl?: string;
  legalPersonIdBackUrl?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  companySize?: number;
  websiteUrl?: string;
  extraCertUrls?: string[];
  usageDescription?: string;
}

export interface CertSubmitResponse {
  certId: string;
  certStatus: CertStatus;
  submissionCount: number;
  submittedAt?: string | null;
}

export interface CertQuota {
  limit: number;
  used: number;
  remaining?: number | null;
  canApply: boolean;
}

export interface CertUploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  expiresIn: number;
}

// ── 权限常量 ─────────────────────────────────────────────────────

export const ORG_MEMBER_ROLE_LABELS: Record<OrgMemberRole, string> = {
  1: '超级管理员',
  2: '管理员',
  3: '普通成员',
};

export const ROLE_TEMPLATE_LABELS: Record<RoleTemplateCode, string> = {
  producer: '制片人',
  executive_producer: '总制片',
  finance: '财务',
};

export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  1: '企业',
  2: '独立团队',
};

export const CERT_STATUS_LABELS: Record<CertStatus, string> = {
  0: '草稿',
  1: '待审核',
  2: '审核中',
  3: '已通过',
  4: '已拒绝',
  5: '待补材料',
};
